import React, { createContext, useState, useContext, useEffect } from 'react';
import { translations } from './translations';

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  // Detect browser language or get from localStorage
  const getBrowserLanguage = () => {
    const stored = localStorage.getItem('cift_lang');
    if (stored) return stored;
    
    // Browser detection - default to 'de' if browser language starts with 'de'
    const browserLang = navigator.language || navigator.userLanguage;
    return browserLang.startsWith('de') ? 'de' : 'en';
  };

  const [language, setLanguage] = useState(getBrowserLanguage());

  // Update document meta data when language changes
  useEffect(() => {
    localStorage.setItem('cift_lang', language);
    
    // Update HTML lang attribute for SEO
    document.documentElement.lang = language;
    
    // Update page title
    const titles = {
      en: 'CIFT Studio – Photography & Film',
      de: 'CIFT Studio – Fotografie & Film'
    };
    document.title = titles[language];
    
    // Update meta description
    const descriptions = {
      en: 'CIFT Studio is a creative photo and video duo specializing in commercial productions, image films, and wedding photography worldwide.',
      de: 'CIFT Studio ist ein kreatives Foto- und Videoduo spezialisiert auf Commercial-Produktionen, Imagefilme und Hochzeitsfotografie weltweit.'
    };
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', descriptions[language]);
    }
    
    // Update OG meta tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', titles[language]);
    }
    
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', descriptions[language]);
    }
    
    // Update Twitter meta tags
    const twitterTitle = document.querySelector('meta[property="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', titles[language]);
    }
    
    const twitterDescription = document.querySelector('meta[property="twitter:description"]');
    if (twitterDescription) {
      twitterDescription.setAttribute('content', descriptions[language]);
    }
  }, [language]);

  const switchLanguage = (lang) => {
    setLanguage(lang);
  };

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      value = value?.[k];
    }
    
    return value || key;
  };

  return (
    <LanguageContext.Provider value={{ language, switchLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useTranslation = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useTranslation must be used within a LanguageProvider');
  }
  return context;
};
